import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Icon } from './Layout'
import VerifiedBadge from './VerifiedBadge'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { votePoll, getPollResult, resharePost, recordPostView, deletePost as apiDeletePostRow } from '../services/posts'
import { likeComment as apiLikeComment } from '../services/comments'
import { adminDeletePost } from '../services/admin'
import { renderTextContent } from '../utils/renderContent'

const EMOJI_TABS = [
  { label: '😀', emojis: ['😀','😂','🤣','😅','😊','😍','🥰','😎','🤩','🥳','😭','😤','🤔','🤯','😱','🥺','😴','🤑','😈','🫡','🫠','😬','🙃','😏','😒'] },
  { label: '❤️', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❣️','💕','💞','💓','💗','💖','💝','💘','💟','🫀','♥️','❤️‍🔥','❤️‍🩹','🩷','🩵','🩶'] },
  { label: '🎉', emojis: ['🎉','🎊','🎈','🎁','🏆','🥇','🎯','🎮','🕹️','🎲','🧩','🎭','🎨','🎬','🎤','🎧','🎵','🎶','🎻','🥁','🪗','🎷','🎸','🎺','🪘'] },
  { label: '💰', emojis: ['💰','💵','💴','💶','💷','💸','💳','🤑','💹','📈','📉','🏦','💎','🪙','₿','🔑','🗝️','🏧','💱','🪂','🚀','🌕','📊','🔐','🛡️'] },
  { label: '🔥', emojis: ['🔥','⚡','✨','💥','🌟','⭐','🌈','🌊','🌀','❄️','🌙','☀️','🌍','🚀','💫','🌌','🪐','☄️','🔮','🧲','⚗️','🌋','🌪️','🌩️','🫧'] },
]

function EmojiPicker({ textareaRef, onClose }) {
  const [activeTab, setActiveTab] = useState(0)
  const pickerRef = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart ?? ta.value.length
    const end = ta.selectionEnd ?? ta.value.length
    const newVal = ta.value.slice(0, start) + emoji + ta.value.slice(end)
    // Use native input setter so React state syncs
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set
    nativeInputValueSetter.call(ta, newVal)
    ta.dispatchEvent(new Event('input', { bubbles: true }))
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + emoji.length; ta.focus() }, 0)
  }

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full mb-2 left-0 z-50 bg-surface-container border-2 border-on-background neo-shadow-sm w-72 p-3 select-none"
      onMouseDown={e => e.preventDefault()}
    >
      {/* Tabs */}
      <div className="flex gap-1 mb-2 border-b-2 border-on-background/10 pb-2 overflow-x-auto">
        {EMOJI_TABS.map((tab, i) => (
          <button
            key={i}
            onMouseDown={e => { e.preventDefault(); setActiveTab(i) }}
            className={`shrink-0 text-base px-2 py-1 transition-colors ${activeTab === i ? 'bg-primary-container text-on-primary-fixed' : 'hover:bg-surface-container-high'}`}
          >{tab.label}</button>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-8 gap-0.5 max-h-40 overflow-y-auto">
        {EMOJI_TABS[activeTab].emojis.map((em, i) => (
          <button
            key={i}
            onMouseDown={e => { e.preventDefault(); insertEmoji(em) }}
            className="text-lg p-1 hover:bg-primary-container/30 transition-colors leading-none"
          >{em}</button>
        ))}
      </div>
    </div>
  )
}

function Comment({ comment, depth = 0 }) {
  const { user } = useAuth()
  const [liked, setLiked] = useState(comment.isLiked ?? comment.likedByMe ?? comment.liked ?? false)
  const [likeCount, setLikeCount] = useState(comment.likes ?? comment.likesCount ?? 0)
  const [showReply, setShowReply] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replies, setReplies] = useState(comment.replies ?? [])
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const replyRef = useRef(null)

  const currentUserIds = new Set([user?.id, user?.profileId, user?.userId, user?._id].filter(Boolean))
  const commentAuthorIds = new Set([comment.author?.id, comment.author?.userId, comment.author?._id].filter(Boolean))
  const isOwnComment = [...currentUserIds].some((id) => commentAuthorIds.has(id))

  const handleLike = async () => {
    if (isOwnComment) return
    const prevLiked = liked
    const prevCount = likeCount
    // Optimistic update
    setLiked(!prevLiked)
    setLikeCount(prevLiked ? prevCount - 1 : prevCount + 1)
    try {
      await apiLikeComment(comment.id)
    } catch {
      // Revert on error
      setLiked(prevLiked)
      setLikeCount(prevCount)
    }
  }

  const submitReply = () => {
    if (!replyText.trim()) return
    setReplies(prev => [...prev, {
      id: `r_${Date.now()}`,
      author: user,
      content: replyText,
      likes: 0,
      replies: [],
      timestamp: 'just now',
    }])
    setReplyText('')
    setShowReply(false)
  }

  return (
    <div className={`flex gap-3 ${depth > 0 ? 'ml-8 pl-3 border-l-2 border-primary-container/40' : ''}`}>
      <img src={comment.author?.avatar || '/favicon.jpg'} alt={comment.author?.name || 'User'} className="w-8 h-8 shrink-0 border border-on-background/20 object-cover" />
      <div className="flex-1 min-w-0">
        <div className="bg-surface-container/60 px-3 py-2 border border-on-background/10">
          <span className="font-bold text-xs text-on-background">{comment.author?.name || 'Anonymous'}</span>
          <VerifiedBadge badge={comment.author?.badge} size={12} className="ml-1 align-middle" />
          <span className="text-[10px] text-on-surface-variant font-mono ml-2">{comment.author?.handle || ''}</span>
          <p className="text-sm text-on-background mt-1 leading-relaxed">{renderTextContent(comment.content)}</p>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-4 mt-1 px-1">
          <button
            onClick={handleLike}
            disabled={isOwnComment}
            title={isOwnComment ? "You can't like your own comment" : undefined}
            className={`flex items-center gap-1 text-[11px] font-bold transition-colors ${isOwnComment ? 'text-on-surface-variant/40 cursor-not-allowed' : liked ? 'text-error' : 'text-on-surface-variant hover:text-error'}`}
          >
            <Icon name="favorite" filled={liked} className="text-[14px]" />
            {likeCount > 0 && <span>{likeCount}</span>}
          </button>
          {depth < 2 && (
            <button onClick={() => setShowReply(v => !v)} className="text-[11px] font-bold text-on-surface-variant hover:text-primary-container transition-colors">
              Reply
            </button>
          )}
          <span className="text-[10px] text-on-surface-variant font-mono ml-auto">{comment.timestamp}</span>
        </div>

        {/* Reply input */}
        {showReply && (
          <div className="mt-2 flex gap-2 items-start relative">
            <img src={user?.avatar || "/favicon.jpg"} alt="me" className="w-6 h-6 shrink-0 border border-on-background/20 object-cover mt-1" />
            <div className="flex-1 relative">
              <textarea
                ref={replyRef}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply() } }}
                placeholder={`Reply to ${comment.author?.name || 'User'}…`}
                rows={2}
                className="w-full bg-background border border-on-background/20 focus:border-primary-container focus:outline-none px-3 py-2 text-xs resize-none text-on-surface"
              />
              <div className="flex justify-between items-center mt-1">
                <div className="relative">
                  <button onClick={() => setShowEmojiPicker(v => !v)} className="hover:text-primary-container transition-colors text-on-surface-variant">
                    <Icon name="sentiment_satisfied" className="text-[18px]" />
                  </button>
                  {showEmojiPicker && <EmojiPicker textareaRef={replyRef} onClose={() => setShowEmojiPicker(false)} />}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setShowReply(false); setReplyText('') }} className="text-[11px] text-on-surface-variant hover:underline px-2 py-1">Cancel</button>
                  <button onClick={submitReply} disabled={!replyText.trim()} className="px-3 py-1 text-[11px] font-bold bg-primary-container text-on-primary-fixed border border-on-background/20 disabled:opacity-40 hover:brightness-105 active:scale-95 transition-all">Reply</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nested replies */}
        {replies.length > 0 && (
          <div className="mt-3 flex flex-col gap-3">
            {replies.map(reply => <Comment key={reply.id} comment={reply} depth={depth + 1} />)}
          </div>
        )}
      </div>
    </div>
  )
}

function PollBlock({ poll, postId }) {
  const [voted, setVoted] = useState(!!poll.userVotedOptionId)
  const [votedId, setVotedId] = useState(poll.userVotedOptionId ?? null)
  const [options, setOptions] = useState(poll.options)
  const [totalVotes, setTotalVotes] = useState(poll.totalVotes ?? 0)
  const [voting, setVoting] = useState(false)

  const expired = poll.expiresAt ? new Date(poll.expiresAt) < new Date() : false
  const showResults = voted || expired
  useEffect(() => {
    if (!expired || !poll.id) return
    getPollResult(poll.id)
      .then(res => {
        const data = res?.data ?? res
        if (!data) return
        const rawOptions = data.options ?? data.Options ?? []
        const total = rawOptions.reduce((acc, o) => acc + (o.voteCount ?? o.votes ?? 0), 0)
        setTotalVotes(total)
        setOptions(rawOptions.map(o => ({
          id: o.id,
          text: o.text ?? o.label ?? o.option ?? '',
          voteCount: o.voteCount ?? o.votes ?? 0,
          pct: total > 0 ? Math.round(((o.voteCount ?? o.votes ?? 0) / total) * 100) : 0,
        })))
      })
      .catch(() => {}) // silently fall back to post-payload data
  }, [expired, poll.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = async (optionId) => {
    if (voted || voting || expired) return
    setVoting(true)
    // Optimistic
    const newTotal = totalVotes + 1
    setOptions(prev => prev.map(o => {
      const newCount = o.id === optionId ? o.voteCount + 1 : o.voteCount
      return { ...o, voteCount: newCount, pct: Math.round((newCount / newTotal) * 100) }
    }))
    setTotalVotes(newTotal)
    setVoted(true)
    setVotedId(optionId)
    try {
      await votePoll(poll.id, optionId)
    } catch {
      // Revert
      setOptions(poll.options)
      setTotalVotes(poll.totalVotes)
      setVoted(false)
      setVotedId(null)
    } finally {
      setVoting(false)
    }
  }

  const timeLeft = () => {
    if (!poll.expiresAt) return null
    const diff = new Date(poll.expiresAt) - new Date()
    if (diff <= 0) return 'Ended'
    const h = Math.floor(diff / 3600000)
    const d = Math.floor(h / 24)
    if (d > 0) return `${d}d left`
    if (h > 0) return `${h}h left`
    return `${Math.floor(diff / 60000)}m left`
  }

  return (
    <div className="mb-4 border-2 border-primary-container/40 p-3 bg-background">
      <p className="font-bold text-sm text-on-background mb-3">{poll.question}</p>
      <div className="flex flex-col gap-2">
        {options.map(opt => (
          <button
            key={opt.id}
            onClick={() => handleVote(opt.id)}
            disabled={showResults || voting}
            className={`relative w-full text-left px-3 py-2 border-2 text-sm font-bold overflow-hidden transition-all
              ${showResults
                ? votedId === opt.id
                  ? 'border-primary-container text-on-background'
                  : 'border-on-background/20 text-on-surface-variant'
                : 'border-on-background/30 hover:border-primary-container text-on-background cursor-pointer active:scale-[0.99]'
              }`}
          >
            {/* Progress bar fill */}
            {showResults && (
              <span
                className={`absolute inset-0 transition-all duration-500 ${votedId === opt.id ? 'bg-primary-container/30' : 'bg-on-background/5'}`}
                style={{ width: `${opt.pct}%` }}
              />
            )}
            <span className="relative flex justify-between items-center gap-2">
              <span className="flex items-center gap-2">
                {showResults && votedId === opt.id && (
                  <Icon name="check_circle" filled className="text-[14px] text-primary-container shrink-0" />
                )}
                {opt.text}
              </span>
              {showResults && (
                <span className="text-xs font-mono shrink-0 text-on-surface-variant">{opt.pct}%</span>
              )}
            </span>
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-2">
        <span className="text-[10px] text-on-surface-variant font-mono">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
        {timeLeft() && (
          <span className="text-[10px] text-primary-container font-mono">⏱ {timeLeft()}</span>
        )}
      </div>
    </div>
  )
}

function GalleryModal({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, images.length - 1))
      if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0))
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [images.length, onClose])

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
      >
        <Icon name="close" className="text-[32px]" />
      </button>
      {idx > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((i) => i - 1) }}
          className="absolute left-4 text-white/70 hover:text-white z-10"
        >
          <Icon name="chevron_left" className="text-[40px]" />
        </button>
      )}
      <img
        src={images[idx]}
        alt=""
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {idx < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((i) => i + 1) }}
          className="absolute right-4 text-white/70 hover:text-white z-10"
        >
          <Icon name="chevron_right" className="text-[40px]" />
        </button>
      )}
      <div className="absolute bottom-4 text-white/50 text-sm font-mono">
        {idx + 1} / {images.length}
      </div>
    </div>
  )
}

function MediaGallery({ images, videos }) {
  const [modalIdx, setModalIdx] = useState(null)
  const MAX_VISIBLE = 4
  const visibleImgs = images.slice(0, MAX_VISIBLE)
  const overflow = images.length - MAX_VISIBLE

  // Grid layout class based on total visible items
  const total = visibleImgs.length + videos.length
  const gridCls =
    total === 1 ? 'grid grid-cols-1' :
    total === 2 ? 'grid grid-cols-2 gap-1' :
    total === 3 ? 'grid grid-cols-2 gap-1' :
                  'grid grid-cols-2 gap-1'

  return (
    <>
      {modalIdx !== null && (
        <GalleryModal
          images={images}
          startIndex={modalIdx}
          onClose={() => setModalIdx(null)}
        />
      )}
      <div className={`${gridCls} mb-3 lg:mb-4`}>
        {visibleImgs.map((src, i) => {
          const isSpanned = total === 3 && i === 0
          const isLast = i === MAX_VISIBLE - 1 && overflow > 0
          const isSingleMedia = total === 1
          return (
            <button
              key={`img-${i}`}
              onClick={() => setModalIdx(i)}
              className={`relative block overflow-hidden border border-on-background/15 bg-on-background/5 focus:outline-none ${isSpanned ? 'row-span-2' : ''}`}
              style={{
                height: isSingleMedia ? '460px' : '220px',
                width: '100%',
              }}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center bg-surface-container"
                style={{ display: 'block' }}
              />
              {isLast && overflow > 0 && (
                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                  <span className="text-white font-black text-2xl">+{overflow}</span>
                </div>
              )}
            </button>
          )
        })}
        {videos.map((src, i) => (
          <div
            key={`vid-${i}`}
            className="relative border border-on-background/15 bg-on-background/5 overflow-hidden"
            style={{ aspectRatio: '16/9' }}
          >
            <video
              src={src}
              controls
              className="absolute inset-0 w-full h-full object-cover"
              preload="metadata"
            />
          </div>
        ))}
      </div>
    </>
  )
}

const renderPostContent = renderTextContent
const POST_TRUNCATE_LENGTH = 280

export default function PostCard({ post, onLike, onEdit, onDelete, onHashtagClick }) {
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const toast = useToast()
  const menuRef = useRef(null)
  const menuButtonRef = useRef(null)
  const cardRef = useRef(null)
  const hasFiredView = useRef(false)
  const [commentCount] = useState(post.comments ?? 0)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [adminDeleting, setAdminDeleting] = useState(false)
  const [adminDeleted, setAdminDeleted] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const postContent = post.content ?? ''
  const categoryName = post.categoryName ?? post.category?.name ?? null
  const isLongPost = postContent.length > POST_TRUNCATE_LENGTH
  const displayedContent = !isLongPost || expanded
    ? postContent
    : postContent.slice(0, POST_TRUNCATE_LENGTH).trimEnd() + '…'
  const currentUserIds = new Set([user?.id, user?.profileId, user?.userId, user?._id].filter(Boolean))
  const ownerInfo = post.isRepost ? post.repostedBy : post.author
  const authorIds = new Set([ownerInfo?.id, ownerInfo?.userId, ownerInfo?._id].filter(Boolean))
  const isOwner = [...currentUserIds].some((id) => authorIds.has(id))
  const originalAuthorIds = new Set([post.author?.id, post.author?.userId, post.author?._id].filter(Boolean))
  const isOriginalAuthor = [...currentUserIds].some((id) => originalAuthorIds.has(id))
  const isOwnRepost = post.isRepost && isOwner
  const deleteMenuLabel = isOwnRepost ? 'Undo Repost' : 'Delete Post'
  const deleteMenuIcon = isOwnRepost ? 'repeat' : 'delete'
  const [reshared, setReshared] = useState(post.isReshared ?? false)
  const [reshareCount, setReshareCount] = useState(post.reshares ?? 0)
  const [ownReshareId, setOwnReshareId] = useState(
    post.myReshareId ?? (post.isRepost && isOwner ? post.id : null),
  )
  const [undoingReshare, setUndoingReshare] = useState(false)
  const [resharing, setResharing] = useState(false)
  const isResharedNow = post.isReshared || reshared
  const canReshare = !isOriginalAuthor && !isOwner && !isResharedNow
  const canUndoReshare = isResharedNow && !!ownReshareId

  useEffect(() => {
    if (!menuOpen) return

    const handlePointerDown = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!cardRef.current || hasFiredView.current) return
    const node = cardRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && !hasFiredView.current) {
          hasFiredView.current = true
          recordPostView(post.id)
          observer.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [post.id])

  const handleEdit = () => {
    setMenuOpen(false)
    if (typeof onEdit === 'function') {
      onEdit(post)
      return
    }
    toast.info('Edit post action will be available once the backend is connected.')
  }

  const handleDelete = () => {
    setMenuOpen(false)
    if (typeof onDelete === 'function') {
      setShowDeleteConfirm(true)
      return
    }
    toast.info('Delete post action will be available once the backend is connected.')
  }

  const confirmDelete = async () => {
    if (isAdmin && !isOwner) {
      setShowDeleteConfirm(false)
      setAdminDeleting(true)
      try {
        await adminDeletePost(post.id)
        setAdminDeleted(true)
        toast.success('Post removed.')
      } catch {
        toast.error('Could not remove post.')
      } finally {
        setAdminDeleting(false)
      }
      return
    }
    setShowDeleteConfirm(false)
    if (typeof onDelete === 'function') {
      onDelete(post)
      return
    }
    toast.info('Delete post action will be available once the backend is connected.')
  }

  const copyPostId = async () => {
    setMenuOpen(false)
    try {
      await navigator.clipboard.writeText(post.id)
      toast.success('Post ID copied — paste it into Remove Content in the admin panel.')
    } catch {
      toast.error('Could not copy post ID.')
    }
  }

  const handleAvatarClick = () => {
    const authorId = post.author?.id
    if (!authorId) return
    if (user?.id && authorId === user.id) navigate('/profile')
    else navigate(`/profile/${authorId}`)
  }

  const interactionPostId = post.interactionPostId ?? post.id

  const openComments = () => {
    navigate('/comments', { state: { post } })
  }

  const handleReshare = async () => {
    if (resharing || isResharedNow) return // backend blocks double reshare
    setResharing(true)
    setReshared(true)
    setReshareCount((c) => c + 1)
    try {
      const created = await resharePost(interactionPostId)
      if (created?.id) setOwnReshareId(created.id)
    } catch (err) {
      setReshared(post.isReshared ?? false)
      setReshareCount(post.reshares ?? 0)
    } finally {
      setResharing(false)
    }
  }

  const handleUndoReshare = async () => {
    if (undoingReshare || !ownReshareId) return
    setUndoingReshare(true)
    const previousCount = reshareCount
    setReshared(false)
    setReshareCount((c) => Math.max(0, c - 1))
    try {
      if (post.isRepost && isOwner) {
        if (typeof onDelete === 'function') onDelete(post)
      } else {
        await apiDeletePostRow(ownReshareId)
        setOwnReshareId(null)
      }
    } catch (err) {
      setReshared(true)
      setReshareCount(previousCount)
      toast.info(err?.message ?? 'Could not undo repost')
    } finally {
      setUndoingReshare(false)
    }
  }

  const handleMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]')
    if (!items || items.length === 0) return

    const currentIndex = Array.from(items).findIndex((item) => item === document.activeElement)
    const nextIndex = event.key === 'ArrowDown'
      ? (currentIndex + 1) % items.length
      : (currentIndex <= 0 ? items.length - 1 : currentIndex - 1)

    items[nextIndex]?.focus()
  }

  if (adminDeleted) return null

  return (
    <>
      <article ref={cardRef} className="bg-surface-container neo-border neo-shadow p-3 lg:p-6 relative lg:flex lg:flex-col lg:h-full">
        {/* Repost banner */}
        {post.isRepost && (
          <div className="flex items-center gap-1.5 mb-2 text-on-surface-variant">
            <Icon name="repeat" className="text-[14px]" />
            <span className="text-[11px] font-bold uppercase tracking-wide">
              {post.repostedBy?.name || 'Someone'}
            </span>
            <VerifiedBadge badge={post.repostedBy?.badge} size={11} />
            <span className="text-[11px] font-bold uppercase tracking-wide">reposted</span>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-start gap-3 mb-3 lg:mb-4">
          <div className="flex gap-2 lg:gap-4 min-w-0">
            <button onClick={handleAvatarClick} className="shrink-0 focus:outline-none group">
              <img
                src={post.author?.avatar || '/favicon.jpg'} alt={post.author?.name || 'User'}
                className="w-9 h-9 lg:w-12 lg:h-12 border border-on-background/20 lg:neo-border object-cover group-hover:brightness-90 group-hover:ring-2 group-hover:ring-primary-container transition-all"
              />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1 lg:gap-2 min-w-0 flex-wrap">
                <button onClick={handleAvatarClick} className="font-bold text-on-background text-sm lg:text-base hover:text-primary-container transition-colors truncate">
                  {post.author?.name || 'Anonymous'}
                </button>
                <VerifiedBadge badge={post.author?.badge} size={14} />
                <span className="text-[11px] text-on-surface-variant font-mono truncate">{post.author?.handle || ''}</span>
                {categoryName && (
                  <span className="inline-flex items-center rounded-full border border-primary-container/40 bg-primary-container/10 px-1.5 py-0.5 text-[9px] lg:text-[10px] font-bold uppercase tracking-[0.12em] text-primary-container">
                    {categoryName}
                  </span>
                )}
              </div>
              <span className="text-[11px] text-on-surface-variant font-mono">{post.timestamp}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {post.trending && (
              <div className="px-2 lg:px-3 py-0.5 lg:py-1 bg-primary-container border border-on-background/20 lg:neo-border flex items-center gap-1">
                <Icon name="bolt" filled className="text-[12px] lg:text-[14px] text-on-primary-fixed" />
                <span className="text-[10px] lg:text-[11px] font-bold text-on-primary-fixed uppercase tracking-wider">Trending</span>
              </div>
            )}
            {(isOwner || isAdmin) && (
              <div className="relative" ref={menuRef}>
                <button
                  ref={menuButtonRef}
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Open post options"
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-on-background/15 bg-surface-container hover:bg-primary-container/10 focus:outline-none focus:ring-2 focus:ring-primary-container"
                >
                  <Icon name="more_horiz" className="text-[18px] text-on-background" />
                </button>

                {menuOpen && (
                  <>
                    {createPortal(
                      <>
                        <div className="fixed inset-0 z-40 bg-black/10 md:hidden" onClick={() => setMenuOpen(false)} />
                        <div
                          role="menu"
                          aria-label="Post actions"
                          tabIndex={-1}
                          onKeyDown={handleMenuKeyDown}
                          className="fixed inset-x-0 bottom-0 z-[9999] md:hidden bg-surface-container border-t border-on-background/15 p-2"
                          style={{ paddingBottom: 'max(8px, env(safe-area-inset-bottom))' }}
                        >
                          <div className="mx-auto max-w-md rounded-2xl bg-surface-container">
                            {!post.isRepost && isOwner && (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={handleEdit}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10"
                              >
                                <Icon name="edit" className="text-[18px]" />
                                <span>Edit Post</span>
                              </button>
                            )}
                            <button
                              type="button"
                              role="menuitem"
                              onClick={copyPostId}
                              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10"
                            >
                              <Icon name="content_copy" className="text-[18px]" />
                              <span>Copy Post ID</span>
                            </button>
                            {!isOwnRepost && (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setMenuOpen(false)
                                  setShowDeleteConfirm(true)
                                }}
                                disabled={adminDeleting}
                                className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10"
                              >
                                <Icon name={deleteMenuIcon} className="text-[18px]" />
                                <span>{deleteMenuLabel}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </>,
                      document.body
                    )}

                    <div className="absolute right-0 top-full z-50 mt-2 hidden min-w-48 rounded-xl border border-on-background/15 bg-surface-container shadow-xl md:block">
                      {!post.isRepost && isOwner && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={handleEdit}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10"
                        >
                          <Icon name="edit" className="text-[18px]" />
                          <span>Edit Post</span>
                        </button>
                      )}
                      <button
                        type="button"
                        role="menuitem"
                        onClick={copyPostId}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10"
                      >
                        <Icon name="content_copy" className="text-[18px]" />
                        <span>Copy Post ID</span>
                      </button>
                      {!isOwnRepost && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setMenuOpen(false)
                            setShowDeleteConfirm(true)
                          }}
                          disabled={adminDeleting}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10 focus:outline-none focus:bg-error/10"
                        >
                          <Icon name={deleteMenuIcon} className="text-[18px]" />
                          <span>{deleteMenuLabel}</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Content — aligned to avatar left edge */}
        <p className={`text-sm lg:text-body-lg font-body-lg leading-relaxed text-on-background whitespace-pre-wrap ${isLongPost ? 'mb-1' : 'mb-3 lg:mb-4'}`}>{renderPostContent(displayedContent, onHashtagClick)}</p>
        {isLongPost && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setExpanded(v => !v) }}
            className="mb-3 lg:mb-4 text-[12px] lg:text-[13px] font-bold text-primary-container hover:underline"
          >
            {expanded ? 'See less' : 'See more'}
          </button>
        )}

        {/* Media Gallery — aligned to avatar left/right edges (no bleed, no extra indent) */}
        {(post.images?.length > 0 || post.videos?.length > 0) && (
          <div className="w-full">
            <MediaGallery images={post.images ?? []} videos={post.videos ?? []} />
          </div>
        )}

        {/* Poll */}
        {post.poll && <PollBlock poll={post.poll} postId={post.id} />}

        {/* Action Bar */}
        <div className="flex justify-between pt-3 lg:pt-4 border-t border-on-background/10 lg:mt-auto">
          <div className="flex items-center gap-5 lg:gap-8">
            <button
              className="flex items-center gap-1.5 group transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => { if (!isOriginalAuthor) onLike(interactionPostId) }}
              disabled={isOriginalAuthor}
              title={isOriginalAuthor ? "You can't like your own post" : undefined}
            >
              <Icon name="favorite" filled={post.liked} className={`text-[18px] lg:text-[24px] text-on-surface-variant ${isOriginalAuthor ? '' : 'group-hover:text-error'} transition-colors ${post.liked ? 'text-error' : ''}`} />
              <span className="text-[11px] lg:text-[12px] text-on-surface-variant">{(() => { const n = Number(post.likes) || 0; return n >= 1000 ? `${(n/1000).toFixed(1)}k` : n || '' })()} </span>
            </button>
            <button
              className="flex items-center gap-1.5 group transition-all"
              onClick={openComments}
            >
              <Icon name="chat_bubble" className="text-[18px] lg:text-[24px] text-on-surface-variant group-hover:text-primary-container transition-colors" />
              <span className="text-[11px] lg:text-[12px] text-on-surface-variant">{commentCount}</span>
            </button>
          </div>

          <div className="flex items-center gap-3 lg:gap-4">
            {/* Three states: can reshare (not yet reshared), can undo
                (already reshared by me and I have the row id to delete),
                or plain count-only (e.g. own original post — reshare count
                shown but nothing to click). */}
            {canReshare ? (
              <button
                className="flex items-center gap-1.5 group transition-all disabled:opacity-50"
                onClick={handleReshare}
                disabled={resharing || isResharedNow}
                title="Reshare this post"
              >
                <Icon
                  name="repeat"
                  className="text-[18px] lg:text-[24px] transition-colors text-on-surface-variant group-hover:text-primary-container"
                />
                <span className="text-[11px] lg:text-[12px] text-on-surface-variant">
                  {reshareCount > 0 ? (reshareCount >= 1000 ? `${(reshareCount/1000).toFixed(1)}k` : reshareCount) : ''}
                </span>
              </button>
            ) : canUndoReshare ? (
              <button
                className="flex items-center gap-1.5 group transition-all disabled:opacity-50"
                onClick={handleUndoReshare}
                disabled={undoingReshare}
                title="Undo repost"
              >
                <Icon
                  name="repeat"
                  className="text-[18px] lg:text-[24px] transition-colors text-primary-container group-hover:text-error"
                />
                <span className="text-[11px] lg:text-[12px] text-primary-container font-bold group-hover:text-error">
                  {reshareCount > 0 ? (reshareCount >= 1000 ? `${(reshareCount/1000).toFixed(1)}k` : reshareCount) : ''}
                </span>
              </button>
            ) : (
              /* Own original post, or reshared elsewhere with no row id
                 known yet — count only, nothing to click. */
              <span className="flex items-center gap-1.5">
                <Icon name="repeat" className={`text-[18px] lg:text-[24px] ${isResharedNow ? 'text-primary-container' : 'text-on-surface-variant/40'}`} />
                <span className={`text-[11px] lg:text-[12px] ${isResharedNow ? 'text-primary-container font-bold' : 'text-on-surface-variant/40'}`}>
                  {reshareCount > 0 ? (reshareCount >= 1000 ? `${(reshareCount/1000).toFixed(1)}k` : reshareCount) : ''}
                </span>
              </span>
            )}
          </div>
        </div>
      </article>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-on-background/15 bg-surface-container p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-bold text-on-background">{isOwnRepost ? 'Undo repost?' : 'Delete post?'}</p>
            <p className="mt-1 text-sm text-on-surface-variant">This action can’t be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-on-background hover:bg-primary-container/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-bold bg-error text-on-error hover:brightness-95"
              >
                {isOwnRepost ? 'Undo Repost' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}